/** GET /report/clients/{clientId}/topics/analytics — one row per published post. */
export const buildPostAnalyticsApiPath = (clientId) => {
  const id = clientId != null ? String(clientId).trim() : ""
  if (!id) return null
  return `/report/clients/${id}/topics/analytics`
}

const hasNumeric = (value) =>
  value != null && value !== "" && Number.isFinite(Number(value))

const metricFromResults = (results, key) => {
  const bucket = results?.[key]
  if (!bucket || typeof bucket !== "object") return null
  const nested = bucket.paid_and_organic ?? bucket.paidAndOrganic
  const raw =
    bucket.latest_value ??
    bucket.latestValue ??
    nested?.latest_value ??
    nested?.latestValue
  if (!hasNumeric(raw)) return null
  return Number(raw)
}

const pickMetric = (item, results, topKey, resultKey) => {
  if (hasNumeric(item?.[topKey])) return Number(item[topKey])
  return metricFromResults(results, resultKey) ?? 0
}

function analyticsPostId(item) {
  const raw = item?.post_id ?? item?.postId
  if (raw == null || raw === "") return null
  return String(raw)
}

function periodRank(period) {
  const key = String(period ?? "").trim().toLowerCase()
  if (key === "month" || key === "custom") return 3
  if (key === "day") return 2
  return 1
}

/** Normalize a single analytics API item for Redux + UI. */
export const normalizePostAnalyticsItem = (item) => {
  const postId = analyticsPostId(item)
  if (!postId) return null

  const results = item.results || {}
  const likes = pickMetric(item, results, "likes", "post_likes")
  const comments = pickMetric(item, results, "comments", "post_comments")
  const impressions = pickMetric(item, results, "impressions", "impressions")
  const reach = metricFromResults(results, "reach") ?? 0
  const views = metricFromResults(results, "post_video_views") ?? 0
  const shares = pickMetric(item, results, "shares", "post_shares")
  const postClicks = metricFromResults(results, "link_clicks") ?? 0
  const totalEngagement = metricFromResults(results, "new_post_engagement") ?? 0
  const engagementRate = metricFromResults(results, "new_engagement_rate") ?? 0

  return {
    postId,
    postLink: item.post_link ?? item.postLink ?? null,
    postDate: item.post_date ?? item.postDate ?? null,
    postMediaLink: item.post_media_link ?? item.postMediaLink ?? null,
    postText: item.post_text ?? item.postText ?? null,
    socialNetwork: item.social_network ?? item.socialNetwork ?? null,
    period: item.period ?? null,
    likes,
    comments,
    impressions,
    reach,
    views,
    shares,
    postClicks,
    totalEngagement,
    engagementRate,
    results,
  }
}

function preferAnalyticsRow(current, next) {
  if (!current) return next
  if (!next) return current
  return periodRank(next.period) > periodRank(current.period) ? next : current
}

/** @param {unknown} response */
export const normalizePostAnalyticsResponse = (response) => {
  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : []
  const byPostId = new Map()
  rows.forEach((item) => {
    const normalized = normalizePostAnalyticsItem(item)
    if (!normalized) return
    byPostId.set(normalized.postId, preferAnalyticsRow(byPostId.get(normalized.postId), normalized))
  })
  return Array.from(byPostId.values())
}

/** Candidate IDs used to match a content post to analytics API rows. */
export const getPostAnalyticsIdCandidates = (post) =>
  [
    post?.networkPostRef,
    post?.platformPostId,
    post?.socialNetworkPostId,
    post?.social_network_post_id,
    post?.externalPostId,
    post?.publishedPostId,
    post?.postId,
    post?.id,
  ]
    .filter((id) => id != null && id !== "")
    .map(String)

function postPermalinkCandidates(post) {
  return [post?.postLink, post?.post_link, post?.permalink, post?.postUrl]
    .filter((link) => typeof link === "string" && link.trim())
    .map((link) => link.trim())
}

function findAnalyticsByLink(rows, post) {
  const links = new Set(postPermalinkCandidates(post))
  if (!links.size) return null
  return rows.find((row) => row?.postLink && links.has(row.postLink)) || null
}

function normalizeMatchText(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim().toLowerCase()
}

function postSocialNetworkKey(post) {
  const raw =
    post?.socialNetworkType?.description ??
    post?.socialNetwork ??
    post?.social_network ??
    post?.platform ??
    ""
  const key = String(raw).trim().toLowerCase()
  if (key === "instagram") return "instagram_graph"
  return key
}

function captionsMatch(a, b) {
  if (!a || !b) return false
  if (a === b) return true
  const left = a.replace(/\.\.\.$/, "").trim()
  const right = b.replace(/\.\.\.$/, "").trim()
  return left.startsWith(right) || right.startsWith(left)
}

function findAnalyticsByCaption(rows, post) {
  const text = normalizeMatchText(post?.postText ?? post?.text ?? post?.caption)
  const network = postSocialNetworkKey(post)
  if (!text || !network) return null
  return (
    rows.find((row) => {
      const rowNetwork = String(row?.socialNetwork ?? "").trim().toLowerCase()
      if (rowNetwork && rowNetwork !== network) return false
      return captionsMatch(text, normalizeMatchText(row?.postText))
    }) || null
  )
}

/**
 * Resolve analytics row for a channel post (Redux map or topicDetails list).
 */
export const resolvePostAnalyticsForPost = (post, engagementMap = {}, topicDetailsList = null) => {
  const candidates = getPostAnalyticsIdCandidates(post)
  const list = Array.isArray(topicDetailsList) ? topicDetailsList : []

  for (const id of candidates) {
    const row = engagementMap[id]
    if (row) return row
  }

  for (const id of candidates) {
    const row = list.find((r) => String(r.postId) === id)
    if (row) return row
  }

  const pooled = [...Object.values(engagementMap), ...list]
  return (
    findAnalyticsByLink(pooled, post) ||
    findAnalyticsByCaption(pooled, post)
  )
}

/**
 * Resolve postLink for a channel from post topics analytics (Redux map or topicDetails).
 * Returns null when the channel has no analytics row or postLink is missing.
 */
export const resolvePostLinkForPost = (post, engagementMap = {}, topicDetailsList = null) => {
  const row = resolvePostAnalyticsForPost(post, engagementMap, topicDetailsList)
  return row?.postLink || null
}

/** Build query string with repeated topicIds params. */
export const buildPostAnalyticsTopicIdsQuery = (topicIds) => {
  const params = new URLSearchParams()
  const ids = Array.isArray(topicIds) ? topicIds : [topicIds]
  ids.forEach((id) => {
    if (id != null && id !== "") params.append("topicIds", String(id))
  })
  return params.toString()
}
